import { z } from 'zod'

// ===========================================
// Contract Status Schema
// ===========================================

export const contractStatusSchema = z.enum(
  ['draft', 'pending_review', 'approved', 'sent', 'signed'],
  {
    required_error: 'Estado del contrato es requerido',
  }
)
export type ContractStatusValue = z.infer<typeof contractStatusSchema>

// ===========================================
// Signature Status Schema
// ===========================================

export const signatureStatusSchema = z.enum(['pending', 'sent', 'signed', 'declined', 'expired'], {
  required_error: 'Estado de firma es requerido',
})
export type SignatureStatusValue = z.infer<typeof signatureStatusSchema>

// ===========================================
// Contract Create Schema
// ===========================================

export const createContractSchema = z.object({
  intakeId: z.string().min(1, 'Orden de contrato es requerida'),
  templateId: z.string().min(1, 'Plantilla es requerida'),
  selectedClauses: z.array(z.string()).default([]),
  fieldOverrides: z.record(z.string(), z.unknown()).default({}),
})

export type CreateContractData = z.infer<typeof createContractSchema>

// ===========================================
// Contract Update Schema
// ===========================================

export const updateContractSchema = z.object({
  selectedClauses: z.array(z.string()).optional(),
  fieldOverrides: z.record(z.string(), z.unknown()).optional(),
  status: contractStatusSchema.optional(),
})

export type UpdateContractData = z.infer<typeof updateContractSchema>

// ===========================================
// Contract Filters Schema
// ===========================================

export const contractFiltersSchema = z.object({
  status: contractStatusSchema.optional(),
  intakeId: z.string().optional(),
  templateId: z.string().optional(),
  search: z.string().optional(),
  page: z.coerce.number().int().positive().default(1),
  limit: z.coerce.number().int().positive().max(100).default(20),
  sortBy: z.enum(['createdAt', 'updatedAt', 'status']).default('createdAt'),
  sortOrder: z.enum(['asc', 'desc']).default('desc'),
})

export type ContractFilters = z.infer<typeof contractFiltersSchema>

// ===========================================
// Contract Approve Schema
// ===========================================

export const approveContractSchema = z.object({
  notes: z.string().optional(),
})

export type ApproveContractData = z.infer<typeof approveContractSchema>

// ===========================================
// API Response Types
// ===========================================

export interface ContractListItem {
  id: string
  intakeId: string
  templateId: string
  status: ContractStatusValue
  version: number
  orderNumber: string
  templateName: string
  propertyAddress: string
  tenantName: string
  createdAt: string
  updatedAt: string
  approvedAt: string | null
  sentAt: string | null
  signedAt: string | null
}

export interface ContractDetail extends ContractListItem {
  dataSnapshot: Record<string, unknown>
  selectedClauses: string[]
  fieldOverrides: Record<string, unknown>
  renderedHtml: string | null
  files: {
    pdf?: string
    docx?: string
  } | null
  approvedBy: string | null
  signatureStatus: SignatureStatusValue | null
  createdBy: string
}

// ===========================================
// Send Email Schema
// ===========================================

export const sendContractEmailSchema = z.object({
  to: z.array(z.string().email('Email invalido')).min(1, 'Se requiere al menos un destinatario'),
  cc: z.array(z.string().email('Email de CC invalido')).optional(),
  subject: z.string().min(1, 'El asunto es requerido').max(200, 'El asunto es muy largo'),
  message: z.string().max(2000, 'El mensaje es muy largo').optional(),
})

export type SendContractEmailData = z.infer<typeof sendContractEmailSchema>
